/*
 * This file is part of glowingblue/orell-bookshelf.
 *
 * Copyright (c) 2022 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import type { SaveAttributes, SaveRelationships } from 'flarum/common/Model';
import type { ApiResponseSingle } from 'flarum/common/Store';
import type Article from 'flarum/orell-articles/models/Article';
import type Bookshelf from '../../common/models/Bookshelf';
import type Note from '../../common/models/Note';
import type Status from '../../common/models/Status';

/** A `null` bookshelf removes `articles[0]` from the bookshelf it currently sits on. */
export default function (
	bookshelf: Bookshelf | null | undefined,
	articles: Article[] = [],
	status?: Status | null,
	note?: Note | null
): Promise<ApiResponseSingle<Bookshelf> | void> {
	const attributes: SaveAttributes = {};
	const relationships: SaveRelationships = {};
	if (bookshelf) {
		relationships.articles = articles;
		if (status) {
			relationships.status = status;
		}
		if (note) {
			// The relationship is only saveable once `id` is present, even as `null`.
			const noteData = note.data as { id?: string | null };
			if (!noteData.id) {
				noteData.id = null;
			}

			relationships.note = note;

			// Not stored on the bookshelf: `EditBookshelfHandler` reads it to set the note's `text`.
			attributes.noteText = note.text();
		}
	} else if (articles[0] && articles[0].bookshelf()) {
		relationships.removedFromArticle = articles[0];
		bookshelf = articles[0].bookshelf() as Bookshelf;
	} else {
		// Nothing to remove the article from.
		return Promise.resolve();
	}

	attributes.relationships = relationships;

	return bookshelf.save(attributes) as Promise<ApiResponseSingle<Bookshelf>>;
}
